import React, { useContext } from 'react';
import { classes } from '@bloomberg/media-ui-core';
import { adOptionsContext } from '@foundation/sparkle/lib/AdOptionsContext';

import { FullWidthAd } from '../FullWidthAd/index';
import { GlobalAdProps } from '../types';

import fullWidthStyles from '../FullWidthAd/styles.module.css';
import styles from './styles.module.css';

export type LeaderboardAdProps = GlobalAdProps & {
  enableParallax?: boolean;
  enableSticky?: boolean;
  isDecoupled?: boolean;
};

export const LeaderboardAd = React.forwardRef<
  HTMLDivElement,
  LeaderboardAdProps
>(function LeaderboardAdComponent(
  {
    name = 'LeaderboardAd',
    className,
    enableParallax = false,
    enableSticky = false,
    isDecoupled = false,
    hideEmptyAd = false,
    ...props
  },
  ref
) {
  const { adOptions: contextAdOptions } = useContext(adOptionsContext);
  const { disableAds } = contextAdOptions || {};

  if (disableAds) return null;

  const fullWidthClassNames = classes([
    fullWidthStyles.fullWidthAdWrapper,
    {
      [styles.hideEmptyAd]: Boolean(hideEmptyAd),
    },
  ]);

  if (enableParallax && enableSticky) {
    const adClassNames = classes([
      styles.leaderboard,
      {
        [styles.stickyParallax]: enableParallax && enableSticky,
      },
      className,
    ]);

    return (
      <div className={fullWidthClassNames}>
        <div className={styles.stickyParallaxWrapper} ref={ref}>
          <FullWidthAd
            name={name}
            adType="leaderboard"
            className={adClassNames}
            ariaLabel="Leaderboard advertisement"
            {...props}
            isDecoupled={isDecoupled}
          />
        </div>
      </div>
    );
  }

  const adClassNames = classes([
    styles.leaderboard,
    {
      [styles.parallax]: enableParallax,
    },
    className,
  ]);

  return (
    <div className={fullWidthClassNames} ref={ref}>
      <FullWidthAd
        name={name}
        adType="leaderboard"
        className={adClassNames}
        ariaLabel="Leaderboard advertisement"
        {...props}
        isDecoupled={isDecoupled}
      />
    </div>
  );
});

export type ContainerWithParallaxLeaderboardProps = LeaderboardAdProps & {
  children: React.ReactNode;
};

export const ContainerWithParallaxLeaderboard = React.forwardRef<
  HTMLDivElement,
  ContainerWithParallaxLeaderboardProps
>(function ContainerWithParallaxLeaderboardComponent(
  { children, ...props },
  ref
) {
  return (
    <div ref={ref}>
      <LeaderboardAd enableParallax {...props} />
      <div className={styles.parallaxWrapper}>{children}</div>
    </div>
  );
});

export const ProtoContainerWithParallaxLeaderboard = React.forwardRef<
  HTMLDivElement,
  ContainerWithParallaxLeaderboardProps
>(function ProtoContainerWithParallaxLeaderboardComponent(
  { children, ...props },
  ref
) {
  return (
    <div ref={ref}>
      <LeaderboardAd enableParallax enableSticky {...props} />
      <div className={styles.parallaxWrapper}>{children}</div>
    </div>
  );
});
